import React from "react";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MapPin, Maximize2, ArrowLeft, CheckCircle, Phone, Mail, Calendar } from "lucide-react";
import { motion } from "framer-motion";

export default function ProjectDetails() {
  const urlParams = new URLSearchParams(window.location.search);
  const projectId = urlParams.get('id');
  const [selectedImage, setSelectedImage] = React.useState(null);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const projects = await base44.entities.Project.list();
      return projects.find(p => p.id === projectId);
    },
    enabled: !!projectId
  });

  React.useEffect(() => { if (project?.featured_image) setSelectedImage(project.featured_image); }, [project]);

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black"></div></div>;
  if (!project) return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><p className="text-xl text-gray-600 mb-4">Projet non trouvé</p><Link to="/projets"><button className="accent-gradient text-white px-6 py-2 rounded-lg">Retour aux projets</button></Link></div></div>;

  const typeLabels = { terrain:"Terrain", maison:"Maison", lotissement:"Lotissement" };
  const allImages = [...(project.featured_image ? [project.featured_image] : []), ...(project.images || [])];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/projets"><button className="mb-6 bg-white/10 text-white border border-white/20 px-4 py-2 rounded-lg hover:bg-white/20 flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Retour aux projets</button></Link>
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }}>
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#c41e3a] text-white px-3 py-1 rounded-full text-sm font-bold">{typeLabels[project.type]}</span>
              <span className="flex items-center gap-2 text-gray-200"><MapPin className="w-4 h-4" />{project.location}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{project.title}</h1>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {selectedImage && <div className="overflow-hidden shadow-xl rounded-xl"><img src={selectedImage} alt={project.title} className="w-full h-96 object-cover" /></div>}
              {allImages.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {allImages.map((image, index) => (
                    <div key={index} onClick={() => setSelectedImage(image)} className={`overflow-hidden cursor-pointer rounded-xl transition-all ${selectedImage === image ? 'ring-4 ring-[#c41e3a]' : 'hover:opacity-75'}`}>
                      <img src={image} alt={`${project.title} ${index + 1}`} className="w-full h-24 object-cover" />
                    </div>
                  ))}
                </div>
              )}
              <div className="border-none shadow-xl p-8 rounded-xl bg-white">
                <h2 className="text-2xl font-bold text-black mb-4">Description</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{project.description}</p>
              </div>
              {project.features?.length > 0 && (
                <div className="border-none shadow-xl p-8 rounded-xl bg-white">
                  <h2 className="text-2xl font-bold text-black mb-4">Caractéristiques</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {project.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" /><span className="text-gray-700">{feature}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="border-none shadow-xl bg-gradient-to-br from-black to-gray-800 text-white p-6 rounded-xl">
                {project.price && <div className="mb-6"><p className="text-gray-300 mb-2">Prix</p><p className="text-4xl font-bold text-[#c41e3a]">{project.price.toLocaleString()} XOF</p></div>}
                {project.area_m2 && <div className="mb-6"><p className="text-gray-300 mb-2">Surface</p><p className="text-2xl font-bold flex items-center gap-2"><Maximize2 className="w-6 h-6" />{project.area_m2} m²</p></div>}
                <div className="pt-6 border-t border-white/20">
                  <div className="flex items-center gap-2 text-gray-200"><Calendar className="w-5 h-5" /><span>Ajouté le {new Date(project.created_date).toLocaleDateString('fr-FR')}</span></div>
                </div>
              </div>
              <div className="border-none shadow-xl p-6 rounded-xl bg-white">
                <h3 className="text-xl font-bold text-black mb-4">Intéressé par ce projet ?</h3>
                <p className="text-gray-600 mb-6">Contactez-nous pour plus d'informations ou pour planifier une visite.</p>
                <div className="space-y-3">
                  <Link to="/contact"><button className="w-full accent-gradient text-white py-3 rounded-lg hover:opacity-90 flex items-center justify-center gap-2"><Mail className="w-4 h-4" />Nous Contacter</button></Link>
                  <button className="w-full border-2 border-black text-black py-3 rounded-lg hover:bg-black hover:text-white flex items-center justify-center gap-2"><Phone className="w-4 h-4" />Appeler</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
